import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Spinner, Table} from "reactstrap";
import {useHistory, useLocation} from "react-router-dom";
import queryParser from "../../helpers/queryStringParser";
import qs from "qs";
import {CLINICIANS_LIMIT} from "../../constants/paginationLimits";
import PaginationTableFooter from "../patientManagement/tableComponents/paginationFooter";
import parseIntFromStr from "../../helpers/parseIntFromStr";
import NoCliniciansPlaceholder from "./cliniciansComponents/noCliniciansPlaceholder";
import {cliniciansColumns} from "./tableComponents/cliniciansDataStructure";
import RoleHistoryModal from "./cliniciansComponents/RoleHistoryModal";
import CancelModal from "../../components/manageSessionModal/cancelModal";
import AppSettingsService from "../../services/appSettings";
import StorageService from "../../services/StorageService";
import {USER} from "../../constants/storageConstants";
import {ROLE_LABELS, SUPER_ADMIN} from "../../constants/roles";
import * as _ from "lodash";
import TableHeader from "../patientManagement/tableComponents/tableHeader";
import SimpleTableBody from "../../components/simpleTable/simpleTableBody";
import CreateClinicianForm from "./cliniciansComponents/createClinicianForm";
import CliniciansService from "../../services/clinicians";
import S3Service from "../../services/S3Service";
import {FILE_TYPES} from "../../constants/uploadFileConstants";
import toastr from "toastr";

const CREATE_SUCCESS_MESSAGE = "The invitation has been successfully sent";
const UPDATE_SUCCESS_MESSAGE = "The information has been successfully updated";

const Clinicians = () => {
    const history = useHistory();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isOpenCreateClinicianModal, setIsOpenCreateClinicianModal] = useState(false);
    const [editClinicianData, setEditClinicianData] = useState(null);
    const [cliniciansPagination, setCliniciansPagination] = useState({});
    const [clinicians, setClinicians] = useState([]);

    const searchQuery = queryParser(location.search)

    const setNewSearchQuery = (incomeQuery) => {
        const newQuery = {
            ...searchQuery,
            ...incomeQuery,
        }
        const query = qs.stringify(newQuery);
        history.push({
            search: query,
        });
    }

    const handleChangePage = ({selected}) => {
        if (!isNaN(selected)) {
            setNewSearchQuery({offset: selected * CLINICIANS_LIMIT});
        }
    }

    const handleEdit = useCallback((admin) => {
        const {
            id,
            firstName,
            lastName,
            email,
            doctor: {
                doctorSignId,
                gmc,
                link,
            },
        } = admin;

        setEditClinicianData({
            id,
            firstName,
            lastName,
            email,
            doctorSignId,
            gmc,
            link,
        })
    }, [setEditClinicianData])

    // superAdmin only: grant a clinician every doctor's patients in the default lists, and change roles.
    const signedIn = new StorageService().getValueByKey(USER);
    const isUserSuperAdmin = signedIn?.role === SUPER_ADMIN;
    const currentUserId = signedIn?.id;

    // A role change, or a "Sign out everywhere" (signOut: true), waiting for confirmation.
    const [pendingRole, setPendingRole] = useState(null);
    const [historyFor, setHistoryFor] = useState(null);

    const toggleFullAccess = useCallback(async (clinician, enabled) => {
        try {
            await AppSettingsService.setFullPatientAccess(clinician.id, enabled);
            setClinicians(previous => previous.map(item => (
                item.id === clinician.id ? { ...item, hasFullPatientAccess: enabled } : item
            )));
        } catch {
            // The interceptor has already shown the error; the checkbox stays as it was.
        }
    }, []);

    // Choosing a role only asks. Whether they look to be mid-consultation is fetched first, so the
    // confirmation can warn that the change will sign them out of the call.
    const requestRoleChange = useCallback(async (clinician, toRole) => {
        let inConsultation = false;
        try {
            const { data } = await AppSettingsService.getClinicianConsultationStatus(clinician.id);
            inConsultation = Boolean(data?.inConsultation);
        } catch {
            // Only a warning: carry on without it.
        }
        setPendingRole({ clinician, toRole, inConsultation });
    }, []);

    const requestSignOut = useCallback(async (clinician) => {
        let inConsultation = false;
        try {
            const { data } = await AppSettingsService.getClinicianConsultationStatus(clinician.id);
            inConsultation = Boolean(data?.inConsultation);
        } catch {
            // Only a warning: carry on without it.
        }
        setPendingRole({ clinician, toRole: clinician.role, inConsultation, signOut: true });
    }, []);

    const showHistory = useCallback((clinician) => setHistoryFor(clinician), []);

    const superAdminTools = useMemo(() => (isUserSuperAdmin ? {
        currentUserId,
        onToggleFullAccess: toggleFullAccess,
        onRequestRoleChange: requestRoleChange,
        onRequestSignOut: requestSignOut,
        onShowHistory: showHistory,
    } : null), [isUserSuperAdmin, currentUserId, toggleFullAccess, requestRoleChange, requestSignOut, showHistory]);

    const { header: tableHeader, structure: dataStructure } = useMemo(
        () => cliniciansColumns({ handleEdit, superAdmin: superAdminTools }),
        [handleEdit, superAdminTools]
    );

    const getClinicians = useCallback(async () => {
        try {
            setIsLoading(true);
            const {data, pagination} = await CliniciansService.getCliniciansList({
                limit: CLINICIANS_LIMIT,
                offset: searchQuery.offset
            });

            setClinicians(data);
            setCliniciansPagination(pagination);
        } finally {
            setIsLoading(false);
        }
    }, [setIsLoading, setClinicians, setCliniciansPagination, searchQuery.offset])

    const handleCreateClinician = useCallback(async (values) => {
        try {
            const {file, ...rest} = values || {};
            const {firstName, lastName, email, gmc} = rest || {};

            const fileData = await S3Service.uploadFile(file, FILE_TYPES.SIGNATURE);

            await CliniciansService.createClinician({
                firstName,
                lastName,
                email,
                gmc,
                doctorSignId: fileData?.id,
            })
            toastr.success(CREATE_SUCCESS_MESSAGE);
            await getClinicians();
        } finally {
            setIsOpenCreateClinicianModal(false);
        }
    }, [setIsOpenCreateClinicianModal])

    const handleUpdateClinician = useCallback(async (values) => {
        try {
            const {id, file, ...rest} = values || {};
            const {firstName, lastName, email, gmc} = rest || {};

            const fileData = await S3Service.uploadFile(file, FILE_TYPES.SIGNATURE);

            await CliniciansService.updateClinician(id, {
                firstName,
                lastName,
                email,
                gmc,
                doctorSignId: fileData?.id,
            })
            toastr.success(UPDATE_SUCCESS_MESSAGE);
            await getClinicians();
        } finally {
            setEditClinicianData(null);
        }
    }, [setEditClinicianData])

    useEffect(() => {
        getClinicians();
    }, [getClinicians])

    const confirmRoleChange = async () => {
        const { clinician, toRole, signOut } = pendingRole;
        setPendingRole(null);

        if (signOut) {
            try {
                await AppSettingsService.signOutClinician(clinician.id);
                toastr.success(`${clinician.firstName} ${clinician.lastName} has been signed out everywhere.`);
            } catch {
                // The interceptor has shown the error.
            }
            return;
        }

        try {
            // The role this page shows goes too: if someone changed it since, the server refuses.
            const { data } = await AppSettingsService.changeClinicianRole(clinician.id, toRole, clinician.role);
            // Merge what the server says, never just the role: a change also withdraws a grant.
            setClinicians(previous => previous.map(item => (
                item.id === clinician.id
                    ? { ...item, role: data.role, hasFullPatientAccess: data.hasFullPatientAccess }
                    : item
            )));
            toastr.success(data.changed
                ? `${clinician.firstName} ${clinician.lastName} is now ${ROLE_LABELS[data.role]} and has been signed out everywhere.`
                : `${clinician.firstName} ${clinician.lastName} has been signed out everywhere.`);
        } catch {
            // The interceptor has shown the error. The role may still have changed (it is changed
            // before the sessions are ended), so reload the list rather than guess.
            await getClinicians();
        }
    };

    const pendingText = () => {
        if (!pendingRole) {
            return '';
        }
        const { clinician, toRole, inConsultation } = pendingRole;
        const name = `${clinician.firstName} ${clinician.lastName}`;
        const consultation = inConsultation
            ? ` ${name} looks to be in a consultation right now: they will be signed out on their next action, `
                + 'anything unsaved will be lost, and ending the call will not close it for the patient. '
                + 'You may want to wait until the call has finished.'
            : '';
        return pendingRole.signOut
            ? `${name} will be signed out of every session, on every device, straight away. `
                + `Their role stays ${ROLE_LABELS[toRole]}.${consultation}`
            : `${name} will change from ${ROLE_LABELS[clinician.role]} to ${ROLE_LABELS[toRole]}, and will be signed `
                + `out of every session, on every device, straight away. They will need to log in again.${consultation}`;
    };

    return (
        <div className="page-content mx-2 pb-2">
            <div className="card justify-content-between pr-20px pl-20px pt-20px activate-code-page">
                <div className="d-flex flex-wrap justify-content-between align-items-end h-100 mb-4">
                    <Col sm={6} lg={4} className="p-0">
                        <label className="details-caption">
                            Clinicians management
                        </label>
                    </Col>
                    <Col sm={5} lg={4} className="p-0 header-button">
                        <button
                            className="btn btn-primary call-form-button w-100  mt-1 mt-sm-0"
                            onClick={() => setIsOpenCreateClinicianModal(true)}
                        >
                            Create new clinician
                        </button>
                    </Col>
                </div>
                {isLoading &&
                    <div className="patients-table d-flex justify-content-center align-items-center">
                        <Spinner className="mx-auto" color="info"/>
                    </div>}
                {!isLoading && clinicians.length > 0 && (
                    <>
                        <section className="activate-code-table-wrap">
                            <Table className="table mb-0">
                                <TableHeader columns={tableHeader} clickCellAction={_.noop}/>
                                <SimpleTableBody data={clinicians} structure={dataStructure}/>
                            </Table>
                        </section>
                        {cliniciansPagination?.totalCount && (
                            <section className="mt-4">
                                <PaginationTableFooter
                                    total={cliniciansPagination.totalCount}
                                    handleChangePage={handleChangePage}
                                    currentOffset={parseIntFromStr(searchQuery.offset)}
                                    limit={CLINICIANS_LIMIT}
                                />
                            </section>
                        )}
                    </>
                )}
                {!isLoading && !clinicians.length && <NoCliniciansPlaceholder/>}
            </div>
            <CreateClinicianForm
                isOpen={isOpenCreateClinicianModal}
                toggle={() => setIsOpenCreateClinicianModal(false)}
                handleSubmit={handleCreateClinician}
            />
            {pendingRole && (
                <CancelModal
                    visibleModal={!!pendingRole}
                    setVisibleModal={() => setPendingRole(null)}
                    handleClose={confirmRoleChange}
                    header={pendingRole.signOut
                        ? "Sign out everywhere?"
                        : `Change role to ${ROLE_LABELS[pendingRole.toRole]}?`}
                    text={pendingText()}
                    cancelButtonTitle="Cancel"
                />
            )}
            {historyFor && <RoleHistoryModal clinician={historyFor} onClose={() => setHistoryFor(null)}/>}
            <CreateClinicianForm
                isEdit
                isOpen={editClinicianData}
                initialValues={editClinicianData}
                toggle={() => setEditClinicianData(null)}
                handleSubmit={handleUpdateClinician}
            />
        </div>
    )
}

export default Clinicians;